import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/components/booking/constants";
import Navbar from "@/components/Navbar";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import BusinessPicker, { businessToAddress } from "@/components/admin/BusinessPicker";
import { enrichWithBusiness } from "@/lib/businessAt";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, Loader2, Plus, Trash2, FileText, Copy, Check, Send, UserCheck, MessageSquare, Camera, ScanLine, AlertTriangle } from "lucide-react";
import { isCbrPickup, isCbrDeliveryTown, CBR_NOTE, CBR_AREA } from "@/components/booking/constants";
import { toast } from "sonner";

const SIZES = [
  { v: "small", l: "Small — under 12.5kg" },
  { v: "medium", l: "Medium — under 16.5kg" },
  { v: "large", l: "Large — under 22.5kg" },
];
const newItem = () => ({ uid: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())), size: "small", quantity: 1, weight: "1", desc: "" });

const STATUS_STYLE = {
  open: "bg-blue-100 text-blue-800", approved: "bg-emerald-100 text-emerald-800",
  booked: "bg-emerald-100 text-emerald-800", declined: "bg-red-100 text-red-700",
  expired: "bg-gray-200 text-gray-600",
};

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [items, setItems] = useState([newItem()]);
  const [service, setService] = useState("standard");
  const [deliveryState, setDeliveryState] = useState("to_deliver");
  const [instructions, setInstructions] = useState("");
  const applyBusiness = (which, b) => {
    const addr = businessToAddress(b);
    (which === "pickup" ? setPickup : setDropoff)(addr);
    if (b.instructions) setInstructions((cur) => {
      const line = `${which === "pickup" ? "Pickup" : "Delivery"} (${b.trade_name}): ${b.instructions}`;
      return cur.includes(line) ? cur : [cur, line].filter(Boolean).join("\n");
    });
    toast.success(`${b.trade_name} filled in as the ${which === "pickup" ? "pickup" : "drop-off"}`);
  };
  const applyPlace = (which, addr) => {
    (which === "pickup" ? setPickup : setDropoff)(addr);
    toast.success(`${addr.business} filled in from Google Maps — check the address`);
  };
  const pickCandidate = (which, c) => {
    (which === "pickup" ? setPickup : setDropoff)((cur) => ({ ...cur, business: c.business, contact_phone: c.contact_phone || cur.contact_phone || "" }));
  };
  const pickAddress = async (which, addr) => {
    const set = which === "pickup" ? setPickup : setDropoff;
    set(addr);
    const rich = await enrichWithBusiness(addr);
    if (rich !== addr) {
      set(rich);
      if (rich.business_instructions) setInstructions((cur) => {
        const line = `${which === "pickup" ? "Pickup" : "Delivery"} (${rich.business}): ${rich.business_instructions}`;
        return cur.includes(line) ? cur : [cur, line].filter(Boolean).join("\n");
      });
      toast.info(`Looks like ${rich.business} trades at this address`);
    }
  };
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const scanRef = useRef(null);
  const scanGalleryRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scans, setScans] = useState([]);

  const scanToItem = (it) => ({ ...newItem(), size: it?.size || "small", quantity: it?.quantity || 1, weight: String(it?.weight_kg || 1), desc: it?.parcel_description || it?.description || "" });
  const applyScan = (s) => {
    if (s.pickup?.lat) setPickup(s.pickup);
    if (s.dropoff?.lat) setDropoff(s.dropoff);
    if (s.item) setItems([scanToItem(s.item)]);
    (s.warnings || []).forEach((w) => toast.warning(w));
  };
  const addBox = (s) => {
    if (!s.item) return;
    setItems((prev) => [...prev, scanToItem(s.item)]);
    toast.success("Box added to this quote");
  };
  const scanLabelText = (s) => {
    const from = s.pickup?.city || s.pickup?.line1 || "?";
    const to = s.dropoff?.city || s.dropoff?.line1 || "?";
    return `${from} → ${to}`;
  };

  const onScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setScanning(true);
    try {
      let entries = [];
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("file", files[0]);
        const { data } = await api.post("/consignment/scan", fd);
        entries = [{ id: Date.now(), pickup: data.pickup, dropoff: data.dropoff, item: (data.items || [])[0], warnings: data.warnings || [] }];
      } else {
        const fd = new FormData();
        files.slice(0, 20).forEach((f) => fd.append("files", f));
        const { data } = await api.post("/consignment/bulk-scan", fd);
        entries = (data.results || []).filter((r) => !r.error).map((r, i) => ({ id: Date.now() + i, pickup: r.pickup, dropoff: r.dropoff, item: (r.items || [])[0], warnings: r.warnings || [] }));
        const failed = (data.results || []).length - entries.length;
        if (failed > 0) toast.warning(`${failed} photo${failed === 1 ? "" : "s"} couldn't be read`);
      }
      if (!entries.length) throw new Error("no readable labels");
      applyScan(entries[0]);
      setScans((prev) => [...entries, ...prev].slice(0, 8));
      toast.success(entries.length > 1 ? `${entries.length} labels read — first applied, the rest are saved below` : "Label read — check the addresses and the live price below");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't read that photo — try a clearer, well-lit shot");
    } finally {
      setScanning(false);
      if (scanRef.current) scanRef.current.value = "";
      if (scanGalleryRef.current) scanGalleryRef.current.value = "";
    }
  };

  const loadQuotes = useCallback(() => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {}), []);
  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  useEffect(() => {
    if (!pickup?.lat || !dropoff?.lat) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote", {
          pickup, dropoff, service,
          items: items.map((i) => ({ size: i.size, quantity: i.quantity, weight_kg: parseFloat(i.weight) || 1 })),
        });
        setPreview(data);
      } catch { setPreview(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [pickup, dropoff, items, service]);

  const setItem = (uid, patch) => setItems((p) => p.map((i) => (i.uid === uid ? { ...i, ...patch } : i)));

  const create = async () => {
    if (!pickup?.lat || !dropoff?.lat) { toast.error("Choose verified pickup and drop-off addresses from the suggestions"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/quotes", {
        pickup, dropoff, service, delivery_state: deliveryState, delivery_instructions: instructions,
        items: items.map((i) => ({ size: i.size, quantity: i.quantity, weight_kg: parseFloat(i.weight) || 1, parcel_description: i.desc })),
      });
      setCreated(data);
      loadQuotes();
      toast.success("Quote created — now assign it or text the link");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't create the quote"); }
    finally { setCreating(false); }
  };

  const reset = () => { setCreated(null); setPickup(null); setDropoff(null); setItems([newItem()]); setPreview(null); setDeliveryState("to_deliver"); setInstructions(""); };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="quote-back-button" className="mb-4 gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Quote builder</h1>
        <p className="mt-1 text-sm text-muted-foreground">Build a quote, then bill it to an account or text the link to a customer. Prices are locked for 7 days.</p>

        {created ? (
          <QuoteActions quote={created} onDone={() => { reset(); loadQuotes(); }} />
        ) : (
          <>
            <Card className="mt-6 border-kilted-green/30 bg-kilted-green/5" data-testid="scan-label-card">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-2">
                  <ScanLine className="h-5 w-5 text-kilted-green" />
                  <div>
                    <p className="text-sm font-semibold text-kilted-green">Snap the label — we'll fill in the rest</p>
                    <p className="text-xs text-muted-foreground">Photo of a box with To/From on it → addresses and item read automatically</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => scanRef.current?.click()} disabled={scanning} data-testid="scan-label-camera-button" className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight">
                    {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Camera
                  </Button>
                  <Button variant="outline" onClick={() => scanGalleryRef.current?.click()} disabled={scanning} data-testid="scan-label-upload-button" className="gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                    {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Upload
                  </Button>
                </div>
                <input ref={scanRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScan} data-testid="scan-label-camera-input" />
                <input ref={scanGalleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onScan} data-testid="scan-label-file-input" />
              </CardContent>
              {scans.length > 0 && (
                <CardContent className="border-t border-kilted-green/20 pt-3" data-testid="scan-history">
                  <p className="mb-2 text-xs font-semibold uppercase text-kilted-green">Recent scans</p>
                  <div className="space-y-1.5">
                    {scans.map((s) => (
                      <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-3 py-1.5 text-sm shadow-sm" data-testid={`scan-entry-${s.id}`}>
                        <span className="font-medium">{scanLabelText(s)}{s.item ? ` · ${s.item.size || "?"} ×${s.item.quantity || 1}` : ""}</span>
                        <span className="flex gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => applyScan(s)} data-testid={`scan-use-${s.id}`} className="h-7 px-2 text-xs">Use</Button>
                          <Button size="sm" variant="outline" onClick={() => addBox(s)} disabled={!s.item} data-testid={`scan-addbox-${s.id}`} className="h-7 px-2 text-xs">+ Add box</Button>
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            <Card className="mt-4">
              <CardHeader><CardTitle className="text-lg">New Quote</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <AddressAutocomplete label="Pickup address" value={pickup} onSelect={(a) => pickAddress("pickup", a)} testid="quote-pickup" />
                <div className="-mt-1 space-y-1">
                  <Label className="text-xs">Business name</Label>
                  <BusinessPicker which="pickup" value={pickup?.business || ""} candidates={pickup?.business_candidates || []} onPick={(b) => applyBusiness("pickup", b)} onPickAddress={(a) => applyPlace("pickup", a)}
                    onPickName={(c) => pickCandidate("pickup", c)} />
                </div>
                {pickup?.contact_name && <p className="-mt-2 text-xs text-muted-foreground" data-testid="quote-pickup-contact">Contact: {pickup.contact_name}{pickup.contact_phone ? ` · ${pickup.contact_phone}` : ""}</p>}
                {!pickup?.contact_name && pickup?.contact_phone && <p className="-mt-2 text-xs text-muted-foreground" data-testid="quote-pickup-contact">Phone: {pickup.contact_phone}</p>}
                <AddressAutocomplete label="Drop-off address" value={dropoff} onSelect={(a) => pickAddress("dropoff", a)} testid="quote-dropoff" />
                <div className="-mt-1 space-y-1">
                  <Label className="text-xs">Business name</Label>
                  <BusinessPicker which="dropoff" value={dropoff?.business || ""} candidates={dropoff?.business_candidates || []} onPick={(b) => applyBusiness("dropoff", b)} onPickAddress={(a) => applyPlace("dropoff", a)}
                    onPickName={(c) => pickCandidate("dropoff", c)} />
                </div>
                {dropoff?.contact_name && <p className="-mt-2 text-xs text-muted-foreground" data-testid="quote-dropoff-contact">Contact: {dropoff.contact_name}{dropoff.contact_phone ? ` · ${dropoff.contact_phone}` : ""}</p>}
                {!dropoff?.contact_name && dropoff?.contact_phone && <p className="-mt-2 text-xs text-muted-foreground" data-testid="quote-dropoff-contact">Phone: {dropoff.contact_phone}</p>}
                <div className="space-y-1"><Label className="text-xs">Driver instructions</Label><Textarea value={instructions} onChange={(e) => setInstructions(e.target.value.slice(0, 400))} rows={2} placeholder="Auto-filled from the business record — add anything else the driver should know" data-testid="quote-instructions" /></div>
                {isCbrPickup(pickup) && (
                  <p className="rounded-md border border-kilted-gold/40 bg-kilted-gold/10 px-2.5 py-2 text-xs text-kilted-green" data-testid="quote-cbr-note">{CBR_NOTE}</p>
                )}
                {isCbrPickup(pickup) && dropoff && !isCbrDeliveryTown(dropoff) && (
                  <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800" data-testid="quote-cbr-warning">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Drop-off is outside the usual Canberra-run area ({CBR_AREA}). Allowed as an admin override — the Canberra morning pickup window still applies.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader><CardTitle className="text-lg">Items & service</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {items.map((it) => (
                  <div key={it.uid} className="grid grid-cols-[minmax(0,1fr)_minmax(0,70px)_minmax(0,80px)_auto] items-end gap-2">
                    <div className="space-y-1"><Label className="text-xs">Size</Label>
                      <Select value={it.size} onValueChange={(v) => setItem(it.uid, { size: v })}>
                        <SelectTrigger data-testid="quote-item-size"><SelectValue /></SelectTrigger>
                        <SelectContent>{SIZES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Qty</Label>
                      <Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(it.uid, { quantity: Math.max(1, parseInt(e.target.value) || 1) })} data-testid="quote-item-qty" />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Weight kg</Label>
                      <Input type="number" min="0.1" step="0.5" value={it.weight} onChange={(e) => setItem(it.uid, { weight: e.target.value })} data-testid="quote-item-weight" />
                    </div>
                    <Button variant="ghost" size="icon" disabled={items.length === 1} onClick={() => setItems((p) => p.filter((x) => x.uid !== it.uid))} data-testid="quote-item-remove"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setItems((p) => [...p, newItem()])} data-testid="quote-add-item" className="gap-1.5"><Plus className="h-4 w-4" /> Add item</Button>
                <div className="grid gap-3 pt-2 sm:grid-cols-2">
                  <div className="space-y-1.5"><Label>Service level</Label>
                    <Select value={service} onValueChange={setService}>
                      <SelectTrigger data-testid="quote-service-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{SERVICES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Delivery status</Label>
                    <RadioGroup value={deliveryState} onValueChange={setDeliveryState} className="gap-1.5 pt-1">
                      <label className="flex cursor-pointer items-center gap-2 text-sm"><RadioGroupItem value="to_deliver" data-testid="quote-state-todeliver" /> To be delivered</label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm"><RadioGroupItem value="delivered" data-testid="quote-state-delivered" /> Already delivered — needs billing approval</label>
                    </RadioGroup>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4 border-kilted-green/20">
              <CardContent className="flex items-center justify-between pt-6">
                <div>
                  <p className="text-sm text-muted-foreground">Quote total (inc GST)</p>
                  <p className="font-heading text-2xl font-extrabold text-kilted-green" data-testid="quote-preview-total">{preview ? `$${preview.total.toFixed(2)}` : "—"}</p>
                  {preview && <p className="text-xs text-muted-foreground">{preview.distance_km} km · fuel levy ${preview.fuel_levy?.toFixed(2) ?? "0.00"} · GST ${preview.gst?.toFixed(2) ?? "0.00"}</p>}
                </div>
                <Button onClick={create} disabled={creating || !preview} data-testid="quote-create-button" className="gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Create quote
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {quotes.length > 0 && (
          <Card className="mt-8">
            <CardHeader><CardTitle className="text-base">Recent quotes</CardTitle></CardHeader>
            <CardContent className="space-y-2" data-testid="recent-quotes-list">
              {quotes.map((q) => (
                <div key={q.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3 text-sm" data-testid={`quote-row-${q.id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{q.tracking_number ? `${q.tracking_number} · ` : ""}{q.pickup.city} → {q.dropoff.city} · ${q.price.total.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(q.created_at).toLocaleDateString("en-GB")}
                      {q.assigned_account_name ? ` · assigned to ${q.assigned_account_name}` : q.sent_phone ? ` · texted ${q.sent_phone}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge className={`${STATUS_STYLE[q.status] || "bg-gray-100 text-gray-700"} capitalize`}>{q.status}</Badge>
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/q/${q.token}`); toast.success("Quote link copied"); }}
                      data-testid="quote-row-copy" className="text-muted-foreground hover:text-kilted-green"><Copy className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function QuoteActions({ quote, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/q/${quote.token}`;

  useEffect(() => { api.get("/quotes/accounts").then((r) => setAccounts(r.data)).catch(() => {}); }, []);

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    toast.success("Quote link copied");
  };

  const assign = async () => {
    if (!accountId) { toast.error("Choose an account customer"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/assign`, { customer_id: accountId });
      if (data.sent) {
        toast.success(`Approval text sent to ${data.assigned_to}`);
        onDone();
      } else {
        toast.warning(`Quote assigned to ${data.assigned_to}, but the text couldn't be sent (SMS provider issue). Copy the link below and send it manually.`, { duration: 8000 });
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't assign the quote"); }
    finally { setBusy(false); }
  };

  const sendSms = async () => {
    const cleaned = phone.replace(/[\s()-]/g, "");
    if (!/^(?:\+?61|0)4\d{8}$/.test(cleaned)) { toast.error("Enter a valid Australian mobile (e.g. 0412 345 678)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send`, { phone: phone.trim() });
      if (data.sent) {
        toast.success(`Quote link texted to ${data.to}`);
        onDone();
      } else {
        toast.warning("The text couldn't be sent (SMS provider issue). Copy the link below and send it manually.", { duration: 8000 });
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send the text"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mt-6 border-kilted-gold/50" data-testid="quote-actions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Check className="h-5 w-5 text-emerald-600" /> Quote created — ${(quote.price_standard || quote.price).total.toFixed(2)} inc GST</CardTitle>
        {quote.price_account && (
          <p className="text-sm font-medium text-kilted-green" data-testid="quote-tier-prices">
            Account customer ${quote.price_account.total.toFixed(2)} · Non-account ${(quote.price_standard || quote.price).total.toFixed(2)}
          </p>
        )}
        <p className="text-sm text-muted-foreground">{quote.pickup.city} → {quote.dropoff.city} · {quote.distance_km} km · valid until {new Date(quote.expires_at).toLocaleDateString("en-GB")}</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2 rounded-lg border border-border p-4">
          <Label className="flex items-center gap-1.5 text-sm font-semibold"><UserCheck className="h-4 w-4 text-kilted-gold" /> Bill to an account holder</Label>
          <p className="text-xs text-muted-foreground">They'll get a text asking to approve it onto their monthly account.</p>
          <div className="flex flex-wrap gap-2">
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="quote-assign-select" className="flex-1"><SelectValue placeholder="Choose account customer" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`quote-account-${a.id}`}>{a.name}{a.phone ? ` · ${a.phone}` : " · no mobile"}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={assign} disabled={busy} data-testid="quote-assign-button" className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send for approval
            </Button>
          </div>
        </div>

        <div className="space-y-2 rounded-lg border border-border p-4">
          <Label className="flex items-center gap-1.5 text-sm font-semibold"><MessageSquare className="h-4 w-4 text-kilted-gold" /> Text the quote to a customer</Label>
          <p className="text-xs text-muted-foreground">They can create an account, view the quote and pay online.</p>
          <div className="flex flex-wrap gap-2">
            <Input type="tel" inputMode="tel" placeholder="0412 345 678" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="quote-phone-input" className="flex-1" />
            <Button onClick={sendSms} disabled={busy} data-testid="quote-send-sms-button" className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Text link
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input readOnly value={link} data-testid="quote-link-input" className="min-w-0 flex-1 text-xs" onFocus={(e) => e.target.select()} />
          <Button variant="outline" onClick={copy} data-testid="quote-copy-link-button" className="shrink-0 gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={onDone} data-testid="quote-new-another-button">Start another quote</Button>
      </CardContent>
    </Card>
  );
}
